import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { $dashboardNotificationStore, $menuStore, $profileFeatureStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { ArrayUtils, DateUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import type { Notification } from '@shared/api/rest/dashboards';
import { SideBar } from './sideBar';
import { NoData } from './noData';
import { Modal } from './modal';
import { Footer } from './footer';
import { Button } from './button';
import { ColorsEnum, Text } from './text';
import { Icon } from './icon';
import NotificationIcon from '@assets/icons/notification.svg';
import NotificationActiveIcon from '@assets/icons/notification-active.svg';
import ChevronIcon from '@assets/icons/chevron.svg';

type LayoutProps = {
  title?: string;
  header?: string | JSX.Element;
  message?: string;
  backText?: string;
  topRightAddons?: JSX.Element;
} & PropsWithChildren;

const DEFAULT_TITLE = 'Loading...';

export const PageContainer = (props: LayoutProps) => {
  const { back } = useRouteHook();

  const isCollapsedMenu = useUnit($menuStore.store);
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const dashboardNotificationStore = useUnit($dashboardNotificationStore.store);

  const [showNotifications, setShowNotifications] = useState<boolean>(false);
  const sideBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = props.title ?? DEFAULT_TITLE;
  }, [props.title]);

  useEffect(() => {
    if (profileFeatureStore.data?.settings.viewNotifications) $dashboardNotificationStore.events.getAll();
  }, [profileFeatureStore]);

  const onClearAllNotifications = () => $dashboardNotificationStore.events.delete({ allNotifications: true });
  const onMarkAllAsReadNotifications = () => $dashboardNotificationStore.events.markAllAsRead({ allNotifications: true });

  const groupedAlerts = useMemo(
    () =>
      ArrayUtils.groupBy<Notification>(
        dashboardNotificationStore.data.filter((notification) => !notification.isRead),
        (n) => n.alertType!,
      ),
    [dashboardNotificationStore.data],
  );
  const isHasAnyUnreadNotifications = useMemo(() => dashboardNotificationStore.data.some((n) => !n.isRead), [dashboardNotificationStore]);
  const isHasAnyNotifications = Object.keys(groupedAlerts).length > 0;
  return (
    <>
      <div className='flex flex-row h-screen min-h-screen w-screen'>
        <SideBar ref={sideBarRef} />
        <div className='flex flex-col flex-1 h-screen min-h-screen overflow-hidden'>
          <div className='min-h-[71px] h-[71px] max-h-[71px] w-full flex px-6 items-center bg-white dark:bg-soft-black border-b border-b-gray-black dark:border-b-gray-wet-asphalt'>
            {props.backText != null ? (
              <div className='inline-flex items-center gap-1 cursor-pointer' onClick={back}>
                <ChevronIcon />
                <Text size='sm'>Go Back</Text>
              </div>
            ) : (
              <>
                {isCollapsedMenu && (
                  <Button color='primary' className='w-8 h-8 mr-6 !p-0 rounded-sm' onClick={() => $menuStore.events.setIsCollapsed(!isCollapsedMenu)}>
                    <Icon icon={<ChevronIcon />} className='rotate-180' color={PaletteUtils.getColor('secondary', 'white')} />
                  </Button>
                )}
                <Text weight='semibold' size='2xl'>
                  {props.header}
                </Text>
                {profileFeatureStore.data?.settings.viewNotifications && (
                  <Button
                    color='icon'
                    className='w-10 !h-10 mx-5 !bg-ice dark:!bg-light-ice rounded-sm relative'
                    onClick={() => setShowNotifications(true)}
                  >
                    <Icon icon={isHasAnyUnreadNotifications ? <NotificationActiveIcon /> : <NotificationIcon />} />
                  </Button>
                )}
              </>
            )}
            {props.topRightAddons && <div className='flex ml-auto'>{props.topRightAddons}</div>}
          </div>
          <div className='flex flex-col h-[calc(100vh-71px)] w-full overflow-y-auto'>
            <div className='flex flex-col w-full p-8'>{props.children}</div>
            <Footer />
          </div>
        </div>
      </div>
      <Modal
        isOpen={showNotifications && profileFeatureStore.data?.settings.viewNotifications === true}
        onClose={() => setShowNotifications(false)}
        title='Notifications'
        width='332px'
      >
        {!isHasAnyNotifications && (
          <div className='mb-5'>
            <NoData text='No new notifications' />
          </div>
        )}
        {Object.keys(groupedAlerts).map((k, i) => {
          const group = groupedAlerts[k];
          const firstGroup = group[0];
          const title = firstGroup.alertTypeDisplayString;
          const text = firstGroup.text;
          const date = firstGroup.createdOn;

          return (
            <div key={i} className='flex flex-col w-full mb-4'>
              <div className='flex flex-col'>
                <div className='flex'>
                  <div className='rounded-full w-3 h-3 my-auto bg-primary mr-1'></div>
                  <Text size='sm'>{title}</Text>
                </div>
                <Text size='xs' color={ColorsEnum.Gray}>
                  {text}
                </Text>
                <Text size='xs' color={ColorsEnum.Gray}>
                  {DateUtils.formatDate(date, { withTime: true })}
                </Text>
              </div>
              <Link className='ml-auto' to={RoutePaths.alerts.onNavigate({ id: groupedAlerts[k][0].alertGuid })}>
                <Button color='outlined'>View all</Button>
              </Link>
            </div>
          );
        })}
        {isHasAnyNotifications && (
          <div className='flex justify-between'>
            {profileFeatureStore.data?.settings.deleteNotifications && (
              <>
                <Button color='outlined' fullWidth onClick={onClearAllNotifications}>
                  Clear all
                </Button>
                <div className='h-full w-6'></div>
              </>
            )}
            <Button color='outlined' fullWidth onClick={onMarkAllAsReadNotifications}>
              Mark all as read
            </Button>
          </div>
        )}
        <Link to={RoutePaths.alerts.onNavigate()}>
          <Button className='mt-6' color='primary' fullWidth>
            History
          </Button>
        </Link>
      </Modal>
    </>
  );
};
